import React, { useState, useEffect } from 'react';
import axios from 'axios';
import showToast from '../../utils/toastHelper';
import ConfirmModal from '../../components/ConfirmModal';

const Members = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showVotingLinkModal, setShowVotingLinkModal] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [elections, setElections] = useState([]);
  const [selectedElection, setSelectedElection] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSendingLink, setIsSendingLink] = useState(false);
  const [showBulkSendModal, setShowBulkSendModal] = useState(false);
  const [bulkSendElection, setBulkSendElection] = useState('');
  const [isBulkSending, setIsBulkSending] = useState(false);
  const [bulkSendProgress, setBulkSendProgress] = useState(null);
  const [csvFile, setCsvFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState([]);
  const [bulkResults, setBulkResults] = useState(null); // results after upload
  const [organization, setOrganization] = useState(null);
  const [groups, setGroups] = useState([]);
  const [formData, setFormData] = useState({
    email: '',
    fullName: '',
    memberCode: '',
    groupId: '',
  });

  const isSchoolMode = organization?.orgType === 'school';

  useEffect(() => {
    fetchOrganization();
    fetchMembers();
    fetchElections();
    fetchGroups();
  }, []);

  const fetchOrganization = async () => {
    try {
      const token = localStorage.getItem('token');
      const userRes = await axios.get(`${process.env.REACT_APP_API_URL}/auth/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (userRes.data.data?.organizationId) {
        const orgRes = await axios.get(`${process.env.REACT_APP_API_URL}/organizations/${userRes.data.data.organizationId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setOrganization(orgRes.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch organization:', error);
    }
  };

  const fetchGroups = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/groups`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGroups(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch groups:', error);
    }
  };

  const fetchMembers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/members`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMembers(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch members:', error);
      showToast('Failed to load members', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchElections = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/elections`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Filter for active closed elections
      const now = new Date();
      const activeClosedElections = (response.data.data || []).filter(election => {
        const startDate = new Date(election.startDate);
        const endDate = new Date(election.endDate);
        return election.accessType === 'closed' && now >= startDate && now <= endDate;
      });
      setElections(activeClosedElections);
    } catch (error) {
      console.error('Failed to fetch elections:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/members`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast('Member added successfully. Invitation email sent.', 'success');
      setShowAddModal(false);
      setFormData({ email: '', fullName: '' });
      fetchMembers();
    } catch (error) {
      console.error('Failed to add member:', error);
      showToast(error.response?.data?.message || 'Failed to add member', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = (member) => {
    setMemberToDelete(member);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${process.env.REACT_APP_API_URL}/members/${memberToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showToast('Member removed successfully', 'success');
      setShowDeleteModal(false);
      setMemberToDelete(null);
      fetchMembers();
    } catch (error) {
      console.error('Failed to remove member:', error);
      showToast('Failed to remove member', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  // Download CSV template based on org mode
  const handleDownloadTemplate = () => {
    let header, exampleRows;
    if (isSchoolMode) {
      header = 'fullName,group,voterId';
      const groupNames = groups.slice(0, 3).map(g => g.name);
      exampleRows = [
        `John Doe,${groupNames[0] || 'S4 Science'},`,
        `Jane Smith,${groupNames[1] || groupNames[0] || 'S4 Arts'},`,
        `Peter Parker,${groupNames[0] || 'S4 Science'},STU-0050`,
      ];
    } else {
      header = 'email,fullName';
      exampleRows = [
        'john@example.com,John Doe',
        'jane@example.com,Jane Smith',
      ];
    }
    const csv = [header, ...exampleRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = isSchoolMode ? 'voters-template.csv' : 'members-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Download results CSV after bulk upload (shows assigned Voter IDs)
  const handleDownloadResults = () => {
    if (!bulkResults) return;
    const rows = ['fullName,voterId,status'];
    bulkResults.added?.forEach(m => rows.push(`"${m.fullName}",${m.memberCode || ''},Added`));
    bulkResults.skipped?.forEach(m => rows.push(`"${m.fullName}",${m.memberCode || ''},Skipped - ${m.reason || ''}`));
    bulkResults.errors?.forEach(m => rows.push(`"${m.fullName || ''}",,Error - ${m.error || ''}`));
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk-upload-results.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      showToast('Please upload a CSV file', 'error');
      return;
    }

    setCsvFile(file);

    // Parse CSV for preview — adapts to org mode
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      if (lines.length < 2) { setCsvPreview([]); return; }

      // Detect columns from header
      const headerCols = lines[0].split(',').map(h => h.trim().toLowerCase());

      const data = lines.slice(1).map(line => {
        // Handle quoted CSV values
        const cols = line.split(',').map(item => item.trim().replace(/^"|"$/g, ''));
        const row = {};
        headerCols.forEach((h, i) => { row[h] = cols[i] || ''; });

        // Normalize column names
        const fullName = row.fullname || row['full name'] || row.name || '';
        const email = row.email || '';
        const groupName = row.group || row.groupname || row['group name'] || row.class || row.department || '';
        const memberCode = row.membercode || row['voter id'] || row['member code'] || row.voterid || '';

        // Validate group name against existing groups
        const groupMatch = groupName ? groups.find(g => g.name.toLowerCase() === groupName.toLowerCase()) : null;
        const groupValid = !groupName || !!groupMatch;

        return { fullName, email, groupName, memberCode, groupValid, groupId: groupMatch?.id || null };
      }).filter(item => item.fullName);

      setCsvPreview(data);
    };
    reader.readAsText(file);
  };

  const handleBulkUpload = async () => {
    if (!csvFile || csvPreview.length === 0) {
      showToast('Please upload a valid CSV file', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/members/bulk`,
        { members: csvPreview },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { data } = response.data;
      showToast(response.data.message, 'success');

      // Store results to show assigned IDs
      setBulkResults(data);
      setCsvFile(null);
      setCsvPreview([]);
      fetchMembers();
    } catch (error) {
      console.error('Failed to bulk upload members:', error);
      showToast(error.response?.data?.message || 'Failed to upload members', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleSendVotingLinkClick = (member) => {
    setSelectedMember(member);
    setSelectedElection('');
    setShowVotingLinkModal(true);
  };

  const handleSendVotingLink = async () => {
    if (!selectedElection) {
      showToast('Please select an election', 'error');
      return;
    }

    setIsSendingLink(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/members/send-voting-link`,
        {
          memberId: selectedMember.id,
          electionId: selectedElection,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast('Voting link sent successfully', 'success');
      setShowVotingLinkModal(false);
      setSelectedMember(null);
      setSelectedElection('');
    } catch (error) {
      console.error('Failed to send voting link:', error);
      showToast(error.response?.data?.message || 'Failed to send voting link', 'error');
    } finally {
      setIsSendingLink(false);
    }
  };

  // Helper: check if member has a real email (not a synthetic .votewave.internal one)
  const hasRealEmail = (member) => member.email && !member.email.endsWith('.votewave.internal');

  // Members with real emails (for bulk send count)
  const membersWithEmail = members.filter(hasRealEmail);

  const handleBulkSendVotingLinks = async () => {
    if (!bulkSendElection) {
      showToast('Please select an election', 'error');
      return;
    }
    setIsBulkSending(true);
    setBulkSendProgress({ sent: 0, total: membersWithEmail.length, failed: 0 });

    let sent = 0;
    let failed = 0;
    const token = localStorage.getItem('token');

    for (const member of membersWithEmail) {
      try {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/members/send-voting-link`,
          { memberId: member.id, electionId: bulkSendElection },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        sent++;
      } catch {
        failed++;
      }
      setBulkSendProgress({ sent: sent + failed, total: membersWithEmail.length, failed });
    }

    showToast(`Sent ${sent} voting link${sent !== 1 ? 's' : ''}${failed > 0 ? `, ${failed} failed` : ''}`, sent > 0 ? 'success' : 'error');
    setIsBulkSending(false);
    setShowBulkSendModal(false);
    setBulkSendElection('');
    setBulkSendProgress(null);
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="mb-1" style={{ fontWeight: '600' }}>Voters</h3>
          <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>Manage voters who can participate in closed elections</p>
        </div>
        <div className="d-flex gap-2">
          {membersWithEmail.length > 0 && elections.length > 0 && (
            <button onClick={() => setShowBulkSendModal(true)} className="btn btn-outline-success" style={{ fontSize: '0.875rem', fontWeight: '500' }}>
              <i className="bi bi-send me-1"></i>Send All Links
            </button>
          )}
          <button onClick={() => setShowBulkModal(true)} className="btn btn-outline-primary" style={{ fontSize: '0.875rem', fontWeight: '500' }}>
            <i className="bi bi-upload me-1"></i>Bulk Upload
          </button>
          <button onClick={() => setShowAddModal(true)} className="btn" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', border: 'none', padding: '0.5rem 1.25rem', fontWeight: '600', fontSize: '0.875rem', borderRadius: '8px' }}>
            <i className="bi bi-plus-circle me-2"></i>Add Voter
          </button>
        </div>
      </div>

      {/* Stats row */}
      <div className="row mb-4">
        {[
          { label: 'Total Voters', value: members.length, icon: 'bi-people', color: '#667eea' },
          { label: 'Active', value: members.filter(m => m.isActive).length, icon: 'bi-person-check', color: '#10b981' },
          { label: 'With Email', value: membersWithEmail.length, icon: 'bi-envelope', color: '#8b5cf6' },
          ...(isSchoolMode ? [{ label: 'Groups', value: groups.length, icon: 'bi-collection', color: '#f59e0b' }] : [{ label: 'Pending', value: members.filter(m => !m.isEmailVerified).length, icon: 'bi-clock', color: '#f59e0b' }]),
        ].map((s, i) => (
          <div key={i} className="col-6 col-lg-3 mb-3">
            <div className="card h-100" style={{ border: 'none', borderLeft: `4px solid ${s.color}` }}>
              <div className="card-body py-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <div className="text-muted" style={{ fontSize: '0.7rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</div>
                    <div className="h4 mb-0 mt-1" style={{ fontWeight: '700' }}>{s.value}</div>
                  </div>
                  <i className={s.icon} style={{ fontSize: '1.75rem', color: s.color, opacity: 0.25 }}></i>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Voter list */}
      <div className="card" style={{ border: 'none' }}>
        <div className="card-body p-0">
          {members.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-people" style={{ fontSize: '3rem', color: '#d1d5db' }}></i>
              <p className="mt-3 text-muted">No voters yet. Add your first voter to get started.</p>
            </div>
          ) : (
            members.map((member, idx) => (
              <div
                key={member.id}
                className="d-flex justify-content-between align-items-center px-4 py-3"
                style={{ borderBottom: idx < members.length - 1 ? '1px solid #f3f4f6' : 'none', transition: 'background 0.15s' }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <div className="d-flex align-items-center" style={{ flex: 1, minWidth: 0 }}>
                  {/* Avatar */}
                  <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: '700', fontSize: '0.9rem', marginRight: '1rem', flexShrink: 0 }}>
                    {member.fullName?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: '600', fontSize: '0.95rem', color: '#1e293b' }}>{member.fullName}</div>
                    <div className="d-flex align-items-center gap-2" style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                      {isSchoolMode && member.memberCode && (
                        <span style={{ fontFamily: 'monospace', color: '#667eea', fontWeight: '600' }}>{member.memberCode}</span>
                      )}
                      {!isSchoolMode && member.email && <span>{member.email}</span>}
                      <span>·</span>
                      <span>{formatDate(member.membershipCreatedAt || member.createdAt)}</span>
                    </div>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2" style={{ flexShrink: 0 }}>
                  {isSchoolMode && member.group && (
                    <span className="badge" style={{ background: member.group.type === 'class' ? '#4f46e5' : '#ea580c', color: 'white', fontSize: '0.7rem', fontWeight: '600' }}>
                      {member.group.name}
                    </span>
                  )}
                  <span className="badge" style={{ background: member.isActive ? '#dcfce7' : '#f3f4f6', color: member.isActive ? '#166534' : '#6b7280', fontSize: '0.7rem', fontWeight: '600' }}>
                    {member.isActive ? 'Active' : 'Inactive'}
                  </span>
                  {hasRealEmail(member) && (
                    <button
                      onClick={() => handleSendVotingLinkClick(member)}
                      style={{ color: '#d1d5db', background: 'none', border: 'none', padding: '0.25rem', cursor: 'pointer', fontSize: '1rem', transition: 'color 0.2s' }}
                      onMouseEnter={(e) => e.currentTarget.style.color = '#667eea'}
                      onMouseLeave={(e) => e.currentTarget.style.color = '#d1d5db'}
                      title="Send Voting Link"
                    >
                      <i className="bi bi-envelope-paper"></i>
                    </button>
                  )}
                  <button
                    onClick={() => handleDeleteClick(member)}
                    style={{ color: '#d1d5db', background: 'none', border: 'none', padding: '0.25rem', cursor: 'pointer', fontSize: '1rem', transition: 'color 0.2s' }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#d1d5db'}
                    title="Remove"
                  >
                    <i className="bi bi-trash"></i>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Add Member Modal */}
      {showAddModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add New Voter</h5>
                <button type="button" className="btn-close" onClick={() => setShowAddModal(false)}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Full Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.fullName}
                      onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">
                      Email {isSchoolMode ? <small className="text-muted">(optional — for sending invites)</small> : '*'}
                    </label>
                    <input
                      type="email"
                      className="form-control"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder={isSchoolMode ? 'Leave blank for ID-only access' : ''}
                      required={!isSchoolMode}
                    />
                  </div>
                  {isSchoolMode && (
                    <>
                      <div className="mb-3">
                        <label className="form-label">Voter ID <small className="text-muted">(optional — auto-generated if empty)</small></label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.memberCode}
                          onChange={(e) => setFormData({ ...formData, memberCode: e.target.value.toUpperCase() })}
                          placeholder="e.g. STU-0001"
                        />
                      </div>
                      {groups.length > 0 && (
                        <div className="mb-3">
                          <label className="form-label">Group <small className="text-muted">(optional)</small></label>
                          <select
                            className="form-select"
                            value={formData.groupId}
                            onChange={(e) => setFormData({ ...formData, groupId: e.target.value })}
                          >
                            <option value="">-- No group --</option>
                            {groups.map(g => (
                              <option key={g.id} value={g.id}>{g.name} ({g.type})</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </>
                  )}
                  <div className="alert alert-info" style={{ fontSize: '0.875rem' }}>
                    <i className="bi bi-info-circle me-2"></i>
                    {isSchoolMode
                      ? (formData.email
                        ? 'An invitation email will be sent. The voter can also vote using their Voter ID.'
                        : 'No email will be sent. The voter will vote by entering their Voter ID.')
                      : 'An invitation email will be sent to the voter with instructions to set up their account.'}
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? 'Adding...' : 'Add Voter'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {showBulkModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-upload me-2"></i>
                  Bulk Upload {isSchoolMode ? 'Voters' : 'Members'}
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowBulkModal(false)}></button>
              </div>
              <div className="modal-body">
                {/* === RESULTS VIEW (after upload) === */}
                {bulkResults ? (
                  <>
                    {/* Summary stats */}
                    <div className="d-flex gap-3 mb-3">
                      {bulkResults.added?.length > 0 && (
                        <div className="flex-fill text-center p-2" style={{ background: '#f0fdf4', borderRadius: '8px' }}>
                          <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#059669' }}>{bulkResults.added.length}</div>
                          <div style={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: '500' }}>Added</div>
                        </div>
                      )}
                      {bulkResults.skipped?.length > 0 && (
                        <div className="flex-fill text-center p-2" style={{ background: '#fefce8', borderRadius: '8px' }}>
                          <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#d97706' }}>{bulkResults.skipped.length}</div>
                          <div style={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: '500' }}>Skipped</div>
                        </div>
                      )}
                      {bulkResults.errors?.length > 0 && (
                        <div className="flex-fill text-center p-2" style={{ background: '#fef2f2', borderRadius: '8px' }}>
                          <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#dc2626' }}>{bulkResults.errors.length}</div>
                          <div style={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: '500' }}>Errors</div>
                        </div>
                      )}
                    </div>

                    {/* Added voters with assigned IDs */}
                    {isSchoolMode && bulkResults.added?.length > 0 && (
                      <div>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h6 style={{ fontSize: '0.875rem', fontWeight: '600', margin: 0 }}>
                            <i className="bi bi-check-circle text-success me-1"></i>
                            Added Voters & Assigned IDs
                          </h6>
                          <button
                            className="btn btn-sm btn-outline-success"
                            onClick={handleDownloadResults}
                            style={{ fontSize: '0.75rem' }}
                          >
                            <i className="bi bi-download me-1"></i>Download Results CSV
                          </button>
                        </div>
                        <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                          <table className="table table-sm">
                            <thead>
                              <tr>
                                <th>Full Name</th>
                                <th>Voter ID</th>
                              </tr>
                            </thead>
                            <tbody>
                              {bulkResults.added.map((m, i) => (
                                <tr key={i}>
                                  <td style={{ fontSize: '0.85rem' }}>{m.fullName}</td>
                                  <td><code style={{ color: '#667eea', fontWeight: '600' }}>{m.memberCode || '—'}</code></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Skipped voters */}
                    {bulkResults.skipped?.length > 0 && (
                      <details className="mt-2" style={{ fontSize: '0.8rem' }}>
                        <summary className="text-muted" style={{ cursor: 'pointer', fontWeight: '500' }}>
                          <i className="bi bi-skip-forward me-1"></i>
                          {bulkResults.skipped.length} skipped
                        </summary>
                        <div className="mt-1">
                          {bulkResults.skipped.map((m, i) => (
                            <div key={i} className="text-muted" style={{ fontSize: '0.8rem' }}>
                              {m.fullName} — <em>{m.reason}</em>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </>
                ) : (
                  /* === UPLOAD FORM VIEW === */
                  <>
                    {/* Step 1: Download template */}
                    <div className="mb-3 p-3" style={{ background: '#f9fafb', borderRadius: '8px', border: '1px dashed #d1d5db' }}>
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong style={{ fontSize: '0.875rem' }}>
                            <i className="bi bi-file-earmark-arrow-down me-1" style={{ color: '#667eea' }}></i>
                            Step 1: Download Template
                          </strong>
                          <p className="mb-0 text-muted" style={{ fontSize: '0.8rem' }}>
                            {isSchoolMode
                              ? 'Fill in voter names and assign them to existing groups. Voter ID is optional (auto-generated if empty).'
                              : 'Fill in emails and full names for each member.'}
                          </p>
                        </div>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={handleDownloadTemplate}
                          style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                        >
                          <i className="bi bi-download me-1"></i>Download CSV
                        </button>
                      </div>
                    </div>

                    {/* Step 2: Upload filled file */}
                    <div className="mb-3">
                      <label className="form-label" style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                        <i className="bi bi-file-earmark-arrow-up me-1" style={{ color: '#667eea' }}></i>
                        Step 2: Upload Filled CSV
                      </label>
                      <input
                        type="file"
                        className="form-control"
                        accept=".csv"
                        onChange={handleFileUpload}
                      />
                      <small className="text-muted">
                        {isSchoolMode
                          ? 'CSV columns: fullName, group, voterId (optional)'
                          : 'CSV columns: email, fullName (with header row)'}
                      </small>
                    </div>

                    {/* Group name validation warnings */}
                    {isSchoolMode && csvPreview.length > 0 && (() => {
                      const invalidGroups = csvPreview.filter(m => m.groupName && !m.groupValid);
                      const uniqueInvalid = [...new Set(invalidGroups.map(m => m.groupName))];
                      return uniqueInvalid.length > 0 ? (
                        <div className="alert alert-warning" style={{ fontSize: '0.8rem' }}>
                          <i className="bi bi-exclamation-triangle me-2"></i>
                          <strong>{uniqueInvalid.length} unrecognised group name{uniqueInvalid.length > 1 ? 's' : ''}:</strong>{' '}
                          {uniqueInvalid.map((name, i) => (
                            <span key={i}><code>{name}</code>{i < uniqueInvalid.length - 1 ? ', ' : ''}</span>
                          ))}
                          <br />
                          <small>These voters will be added without a group. Check spelling matches your Groups page exactly.</small>
                        </div>
                      ) : null;
                    })()}
                    
                    {/* Preview table */}
                    {csvPreview.length > 0 && (
                      <div>
                        <h6 style={{ fontSize: '0.875rem', fontWeight: '600' }}>
                          Preview ({csvPreview.length} {isSchoolMode ? 'voters' : 'members'})
                        </h6>
                        <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                          <table className="table table-sm">
                            <thead>
                              <tr>
                                <th>Full Name</th>
                                {!isSchoolMode && <th>Email</th>}
                                {isSchoolMode && <th>Group</th>}
                                {isSchoolMode && <th style={{ width: '40px' }}></th>}
                              </tr>
                            </thead>
                            <tbody>
                              {csvPreview.slice(0, 15).map((member, index) => (
                                <tr key={index}>
                                  <td>{member.fullName}</td>
                                  {!isSchoolMode && <td style={{ fontSize: '0.85rem', color: '#6b7280' }}>{member.email}</td>}
                                  {isSchoolMode && (
                                    <td>
                                      {member.groupName ? (
                                        <span className={`badge ${member.groupValid ? 'bg-info' : 'bg-danger'}`} style={{ fontSize: '0.7rem' }}>
                                          {member.groupName}
                                        </span>
                                      ) : (
                                        <span className="text-muted" style={{ fontSize: '0.75rem' }}>—</span>
                                      )}
                                    </td>
                                  )}
                                  {isSchoolMode && (
                                    <td>
                                      {member.groupName && !member.groupValid && (
                                        <i className="bi bi-exclamation-circle text-danger" title="Group not found" style={{ fontSize: '0.85rem' }}></i>
                                      )}
                                      {member.groupValid && member.groupName && (
                                        <i className="bi bi-check-circle text-success" style={{ fontSize: '0.85rem' }}></i>
                                      )}
                                    </td>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {csvPreview.length > 15 && (
                            <p className="text-muted text-center" style={{ fontSize: '0.8rem' }}>
                              ... and {csvPreview.length - 15} more
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                    
                    <div className="alert alert-info" style={{ fontSize: '0.8rem' }}>
                      <i className="bi bi-info-circle me-2"></i>
                      {isSchoolMode ? (
                        <>
                          <strong>Voter IDs</strong> will be auto-generated if left empty in the CSV.
                          {groups.length > 0 && (<> Make sure group names <strong>exactly match</strong> the names on your <strong>Groups</strong> page.</>)}
                        </>
                      ) : (
                        'Invitation emails will be sent to all new members automatically.'
                      )}
                    </div>

                    {/* Existing groups reference */}
                    {isSchoolMode && groups.length > 0 && (
                      <details style={{ fontSize: '0.8rem' }}>
                        <summary className="text-muted" style={{ cursor: 'pointer', fontWeight: '500' }}>
                          <i className="bi bi-collection me-1"></i>
                          View available group names ({groups.length})
                        </summary>
                        <div className="mt-2 d-flex flex-wrap gap-1">
                          {groups.map(g => (
                            <span key={g.id} className={`badge ${g.type === 'class' ? 'bg-info' : 'bg-warning'}`} style={{ fontSize: '0.7rem', fontWeight: '500' }}>
                              {g.name}
                            </span>
                          ))}
                        </div>
                      </details>
                    )}
                  </>
                )}
              </div>
              <div className="modal-footer">
                {bulkResults ? (
                  <button type="button" className="btn btn-primary" onClick={() => { setShowBulkModal(false); setBulkResults(null); }}>
                    Done
                  </button>
                ) : (
                  <>
                    <button type="button" className="btn btn-secondary" onClick={() => setShowBulkModal(false)}>
                      Cancel
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-primary" 
                      onClick={handleBulkUpload}
                      disabled={isSubmitting || csvPreview.length === 0}
                    >
                      {isSubmitting ? 'Uploading...' : `Upload ${csvPreview.length} ${isSchoolMode ? 'Voters' : 'Members'}`}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Send Voting Link Modal */}
      {showVotingLinkModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Send Voting Link</h5>
                <button type="button" className="btn-close" onClick={() => setShowVotingLinkModal(false)}></button>
              </div>
              <div className="modal-body">
                <p className="mb-3">
                  Send a secure voting link to <strong>{selectedMember?.fullName}</strong> ({selectedMember?.email})
                </p>
                
                {elections.length === 0 ? (
                  <div className="alert alert-warning">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    No active closed elections available. Voting links can only be sent for closed elections that are currently active.
                  </div>
                ) : (
                  <>
                    <div className="mb-3">
                      <label className="form-label">Select Election *</label>
                      <select
                        className="form-select"
                        value={selectedElection}
                        onChange={(e) => setSelectedElection(e.target.value)}
                      >
                        <option value="">-- Choose an election --</option>
                        {elections.map((election) => (
                          <option key={election.id} value={election.id}>
                            {election.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="alert alert-info" style={{ fontSize: '0.875rem' }}>
                      <i className="bi bi-info-circle me-2"></i>
                      A secure, single-use voting link will be sent to the member's email address.
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowVotingLinkModal(false)}>
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary" 
                  onClick={handleSendVotingLink}
                  disabled={isSendingLink || !selectedElection || elections.length === 0}
                >
                  {isSendingLink ? 'Sending...' : 'Send Voting Link'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Send Voting Links Modal */}
      {showBulkSendModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title"><i className="bi bi-send me-2"></i>Send Voting Links to All</h5>
                <button type="button" className="btn-close" onClick={() => !isBulkSending && setShowBulkSendModal(false)}></button>
              </div>
              <div className="modal-body">
                <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                  Send voting links to <strong>{membersWithEmail.length}</strong> voter{membersWithEmail.length !== 1 ? 's' : ''} with email addresses.
                </p>

                {elections.length === 0 ? (
                  <div className="alert alert-warning">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    No active closed elections available.
                  </div>
                ) : (
                  <div className="mb-3">
                    <label className="form-label">Select Election *</label>
                    <select className="form-select" value={bulkSendElection} onChange={(e) => setBulkSendElection(e.target.value)}>
                      <option value="">-- Choose an election --</option>
                      {elections.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                    </select>
                  </div>
                )}

                {bulkSendProgress && (
                  <div className="mt-3">
                    <div className="d-flex justify-content-between mb-1" style={{ fontSize: '0.8rem' }}>
                      <span>Sending...</span>
                      <span>{bulkSendProgress.sent} / {bulkSendProgress.total}</span>
                    </div>
                    <div className="progress" style={{ height: '8px' }}>
                      <div className="progress-bar" style={{ width: `${(bulkSendProgress.sent / bulkSendProgress.total) * 100}%`, background: 'linear-gradient(90deg, #667eea, #764ba2)' }}></div>
                    </div>
                    {bulkSendProgress.failed > 0 && (
                      <small className="text-danger mt-1 d-block">{bulkSendProgress.failed} failed</small>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowBulkSendModal(false)} disabled={isBulkSending}>Cancel</button>
                <button className="btn btn-success" onClick={handleBulkSendVotingLinks} disabled={isBulkSending || !bulkSendElection || elections.length === 0}>
                  {isBulkSending ? (
                    <><span className="spinner-border spinner-border-sm me-2"></span>Sending...</>
                  ) : (
                    <><i className="bi bi-send me-1"></i>Send to {membersWithEmail.length} Voter{membersWithEmail.length !== 1 ? 's' : ''}</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        show={showDeleteModal}
        title="Remove Member"
        message={`Are you sure you want to remove ${memberToDelete?.fullName}? They will no longer be able to vote in closed elections.`}
        confirmText="Remove"
        confirmVariant="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setShowDeleteModal(false);
          setMemberToDelete(null);
        }}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default Members;
